/** Monaco CompletionItemInsertTextRule.InsertAsSnippet と同値 */
export const INSERT_AS_SNIPPET = 4;

/** シグネチャヘルプ・補完 detail 用の引数1件 */
export interface IndexCoreFormulaParameter {
  /** signature 行に並べる短いラベル（識別用。重複は避ける） */
  readonly label: string;
  /** その引数の役割（Markdown 可・短文推奨） */
  readonly documentation: string;
}

/** Monaco 補完・ホバー用の IndexCore Formula 組み込み関数メタデータ */
export interface IndexCoreFormulaBuiltin {
  /** 関数名（API 上は大文字小文字を問わないが補完では小文字で統一） */
  name: string;
  /**
   * 補完一覧の行内（description）用・一言用途。長すぎると UI で省略されるため ~24 文字程度を目安にする。
   */
  usage: string;
  /** ホバー・詳細ペイン用の短い説明（長文は避ける） */
  documentation: string;
  /** 補完リスト右側の1行要約（シグネチャ風） */
  detail: string;
  /** シグネチャヘルプの各引数（空なら引数なし関数） */
  signatureParameters: readonly IndexCoreFormulaParameter[];
  /** 補完確定時に挿入する文字列（スニペット形式可） */
  insertText: string;
  /** スニペットとして解釈する場合は INSERT_AS_SNIPPET を指定 */
  insertTextRules?: typeof INSERT_AS_SNIPPET;
}

/** シグネチャヘルプ用の表示ラベル（括弧・引数並び） */
export function getFormulaBuiltinSignatureLabel(b: IndexCoreFormulaBuiltin): string {
  if (!b.signatureParameters.length) return `${b.name}()`;
  return `${b.name}(${b.signatureParameters.map((p) => p.label).join(', ')})`;
}

export const INDEXCORE_FORMULA_LANGUAGE_ID = 'indexcore-formula';

/**
 * View の Formula で利用できる関数一覧（IndexCore API ドキュメント準拠）
 * パフォーマンス: 静的配列のため参照のみで O(n) は補完時のフィルタのみ。
 */
export const INDEXCORE_FORMULA_BUILTINS: IndexCoreFormulaBuiltin[] = [
  {
    name: 'alias',
    usage: '元データの値を取る',
    detail: 'alias(fieldKey | viewKey=fieldKey, …)',
    signatureParameters: [
      { label: 'fieldKey | viewKey=fieldKey', documentation: '取得する項目キー。複数で辞書化。`viewKey=fieldKey` でキー名変更。' },
      { label: '…', documentation: '追加の項目（可変長）。' },
    ],
    documentation: '項目の値を取得。複数指定で辞書化し、`viewKey=fieldKey` でキー名を変えられる。',
    insertText: 'alias(${1:fieldKey})',
    insertTextRules: INSERT_AS_SNIPPET,
  },
  {
    name: 'substring',
    usage: '文字の一部だけ使う',
    detail: 'substring(fieldKey, start, length?)',
    signatureParameters: [
      { label: 'fieldKey', documentation: '対象の文字列項目キー。' },
      { label: 'start', documentation: '開始位置（0 基準）。負値は末尾から。' },
      { label: 'length?', documentation: '長さ。省略で末尾まで。' },
    ],
    documentation: '文字列の一部を切り出す。開始は 0 基準、長さ省略で末尾まで。',
    insertText: 'substring(${1:fieldKey}, ${2:0}, ${3:length})',
    insertTextRules: INSERT_AS_SNIPPET,
  },
  {
    name: 'sepstring',
    usage: '区切り文字で分割して取得',
    detail: "sepstring(fieldKey, separator, start, length?)",
    signatureParameters: [
      { label: 'fieldKey', documentation: '区切り分割の対象項目。' },
      { label: "separator", documentation: "区切り文字。`'/` のようにシングルクォートで囲む。" },
      { label: 'start', documentation: '部分取得の開始インデックス。' },
      { label: 'length?', documentation: '長さ。省略可。' },
    ],
    documentation: '区切り文字で配列のように分割し、その一部を取り出す。',
    insertText: "sepstring(${1:fieldKey}, '${2:/}', ${3:0}, ${4:length})",
    insertTextRules: INSERT_AS_SNIPPET,
  },
  {
    name: 'joinstring',
    usage: '複数項目を1文字でつなぐ',
    detail: "joinstring(joinChar, fieldKey1, …)",
    signatureParameters: [
      { label: 'joinChar', documentation: '連結に使う1文字（シングルクォートで囲む）。' },
      { label: 'fieldKey1', documentation: '1つ目の項目キー。' },
      { label: '…', documentation: '追加の項目キー（可変長）。' },
    ],
    documentation: '指定した1文字を挟んで、複数項目の値を1本の文字列にする。',
    insertText: "joinstring('${1:-}', ${2:fieldKey1}, ${3:fieldKey2})",
    insertTextRules: INSERT_AS_SNIPPET,
  },
  {
    name: 'datestring',
    usage: '日付を書式文字列にする',
    detail: "datestring('format', fieldKey?, …)",
    signatureParameters: [
      { label: "'format'", documentation: '日付書式。現在日時または項目値のフォーマットに使用。' },
      { label: 'fieldKey?', documentation: '省略時は現在日時。指定時は項目値をフォーマット。' },
      { label: '…', documentation: '日付加算オプション等（例: addDays）。' },
    ],
    documentation: '現在日時または項目の日付を `yyyyMMdd` などの形式の文字列にする。',
    insertText: "datestring('${1:yyyyMMdd}'${2:, fieldKey})",
    insertTextRules: INSERT_AS_SNIPPET,
  },
  {
    name: 'format',
    usage: 'テンプレに項目を埋め込む',
    detail: "format('…{fieldKey}…')",
    signatureParameters: [
      { label: "'テンプレート'", documentation: "補間文字列。`{fieldKey}` で項目を埋め込む。" },
    ],
    documentation: 'クォート内の `{キー}` を、対応する項目の値で置き換える。',
    insertText: "format('${1:{fieldKey1}-{fieldKey2}}')",
    insertTextRules: INSERT_AS_SNIPPET,
  },
  {
    name: 'case',
    usage: '値を別の値に置き換える',
    detail: 'case(fieldKey, mapping | 1:a, 2:b, default)',
    signatureParameters: [
      { label: 'fieldKey', documentation: '置換の元になる値の項目キー。' },
      { label: 'mapping / 分岐…', documentation: '`options` 形式、または `値:結果` の列と `default`。' },
    ],
    documentation: '項目の値に応じて、別の値やラベルへマッピングする。',
    insertText: 'case(${1:fieldKey}, ${2:options})',
    insertTextRules: INSERT_AS_SNIPPET,
  },
  {
    name: 'array',
    usage: '複数項目を配列にする',
    detail: 'array(fieldKey1, …)',
    signatureParameters: [
      { label: 'fieldKey1', documentation: '配列の1要素目の項目キー。' },
      { label: '…', documentation: '追加の項目キー（可変長）。' },
    ],
    documentation: '並べた項目の値を、1つの配列値として扱う。',
    insertText: 'array(${1:fieldKey1}, ${2:fieldKey2})',
    insertTextRules: INSERT_AS_SNIPPET,
  },
  {
    name: 'calc',
    usage: '四則だけの式を評価',
    detail: 'calc(式)',
    signatureParameters: [
      { label: '式', documentation: '四則演算のみ。項目は裸識別子。他関数は不可（仕様）。' },
    ],
    documentation: '項目同士の加減乗除だけを式で書く（中に別関数は書けない）。',
    insertText: 'calc((${1:fieldKey1}+${2:fieldKey2}))',
    insertTextRules: INSERT_AS_SNIPPET,
  },
  {
    name: 'round',
    usage: '式の値を四捨五入',
    detail: 'round(expression, digits)',
    signatureParameters: [
      { label: 'expression', documentation: '`{fieldKey}` を含む式。' },
      { label: 'digits', documentation: '丸め桁位置。' },
    ],
    documentation: '`{項目}` を含む式を、指定桁で四捨五入する。',
    insertText: 'round(${1:{fieldKey1} + {fieldKey2}}, ${2:0})',
    insertTextRules: INSERT_AS_SNIPPET,
  },
  {
    name: 'ceiling',
    usage: '式の値を切り上げ',
    detail: 'ceiling(expression, digits)',
    signatureParameters: [
      { label: 'expression', documentation: '`{fieldKey}` を含む式。' },
      { label: 'digits', documentation: '桁位置。' },
    ],
    documentation: '式の値を指定桁で切り上げる。',
    insertText: 'ceiling(${1:{fieldKey}}, ${2:0})',
    insertTextRules: INSERT_AS_SNIPPET,
  },
  {
    name: 'floor',
    usage: '式の値を切り捨て',
    detail: 'floor(expression, digits)',
    signatureParameters: [
      { label: 'expression', documentation: '`{fieldKey}` を含む式。' },
      { label: 'digits', documentation: '桁位置。' },
    ],
    documentation: '式の値を指定桁で切り捨てる。',
    insertText: 'floor(${1:{fieldKey}}, ${2:0})',
    insertTextRules: INSERT_AS_SNIPPET,
  },
  {
    name: 'count',
    usage: '行数・件数を数える',
    detail: 'count()',
    signatureParameters: [],
    documentation: '集計単位の行数。GroupBy 時はそのグループ内の件数。',
    insertText: 'count()',
  },
  {
    name: 'sum',
    usage: '数値の合計',
    detail: 'sum(fieldKey?, …)',
    signatureParameters: [
      { label: 'fieldKey?', documentation: '省略時は view 項目名に対応するデータ項目。' },
      { label: '…', documentation: '追加項目やネストした round 等（可変長）。' },
    ],
    documentation: '指定項目（省略時は既定項目）の合計。複数・入れ子も可。',
    insertText: 'sum(${1:fieldKey})',
    insertTextRules: INSERT_AS_SNIPPET,
  },
  {
    name: 'min',
    usage: '最小値を求める',
    detail: 'min(fieldKey?, …)',
    signatureParameters: [
      { label: 'fieldKey?', documentation: '省略時は view に応じた既定。' },
      { label: '…', documentation: '比較対象の追加項目（可変長）。' },
    ],
    documentation: '対象項目の最小値。複数指定でその中の最小。',
    insertText: 'min(${1:fieldKey})',
    insertTextRules: INSERT_AS_SNIPPET,
  },
  {
    name: 'max',
    usage: '最大値を求める',
    detail: 'max(fieldKey?, …)',
    signatureParameters: [
      { label: 'fieldKey?', documentation: '省略時は view に応じた既定。' },
      { label: '…', documentation: '比較対象の追加項目（可変長）。' },
    ],
    documentation: '対象項目の最大値。複数指定でその中の最大。',
    insertText: 'max(${1:fieldKey})',
    insertTextRules: INSERT_AS_SNIPPET,
  },
  {
    name: 'rowNo',
    usage: '行の連番',
    detail: 'rowNo()',
    signatureParameters: [],
    documentation: '表示順などに使う行番号（1 始まり想定の用途が多い）。',
    insertText: 'rowNo()',
  },
  {
    name: 'listagg',
    usage: '集計で値を列挙',
    detail: 'listagg(options, fieldKey1, …)',
    signatureParameters: [
      { label: 'options', documentation: '`list` / `distinct` 等のオプション。' },
      { label: 'fieldKey1', documentation: 'リスト化する1つ目の項目。' },
      { label: '…', documentation: '追加の項目キー（可変長）。' },
    ],
    documentation: 'グループ内の値を区切り付きの1文字列にまとめる（集計用）。',
    insertText: 'listagg(${1:distinct}, ${2:fieldKey})',
    insertTextRules: INSERT_AS_SNIPPET,
  },
  {
    name: 'guid',
    usage: '一意の GUID を発行',
    detail: 'guid()',
    signatureParameters: [],
    documentation: '毎回異なる GUID 文字列を生成する。',
    insertText: 'guid()',
  },
  {
    name: 'ulid',
    usage: '時系列にも強い ULID',
    detail: 'ulid()',
    signatureParameters: [],
    documentation: 'ソートしやすい一意 ID（ULID）を生成する。',
    insertText: 'ulid()',
  },
  {
    name: 'now',
    usage: '現在の日時',
    detail: 'now()',
    signatureParameters: [],
    documentation: 'サーバー／実行タイミングの現在日時を返す。',
    insertText: 'now()',
  },
];
